<script setup lang="ts">
import { Search } from '@lucide/vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { Badge } from '@/components/ui/badge/index';
import { Button } from '@/components/ui/button/index';
import { Input } from '@/components/ui/input/index';

import { useGlobalSearch } from '../composables/useGlobalSearch';

const { t } = useI18n();
const { open } = useGlobalSearch();

/**
 * Detect if user is on Mac/iOS for keyboard shortcut display.
 */
const isMac = computed(() => {
    return /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);
});

/**
 * Keyboard shortcut hint text.
 */
const shortcutHint = computed(() => {
    return isMac.value ? t('globalSearch.shortcutHint') : t('globalSearch.shortcutHintWin');
});

/**
 * Handle click on input - open search dialog.
 */
function handleClick() {
    open();
}
</script>

<template>
    <!-- Narrow viewports: icon-only avoids horizontal overflow in the top bar -->
    <Button
        variant="outline"
        size="icon"
        class="shrink-0 sm:hidden"
        :aria-label="t('globalSearch.placeholder')"
        @click="handleClick"
    >
        <Search class="size-4" />
    </Button>

    <div class="relative hidden min-w-0 max-w-64 cursor-pointer sm:block" @click="handleClick">
        <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Search class="text-muted-foreground size-4" />
        </div>
        <Input readonly :placeholder="t('globalSearch.placeholder')" class="h-9 w-full min-w-0 cursor-pointer pl-9 pr-9 md:pr-16" />
        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <Badge variant="secondary" class="hidden px-1.5 py-0.5 text-[10px] font-medium md:inline-flex">
                {{ shortcutHint }}
            </Badge>
        </div>
    </div>
</template>
